import type { TeamWith } from "@redotech/account-common/base-team";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { RedoFulfillmentTote } from "@redotech/redo-fulfillment-group-common/fulfillment-tote";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { PickSessionStrategy } from "@redotech/redo-fulfillment-wms-common/pick-session-enums";
import {
  PickSession,
  PickSessionItem,
  pickSessionItemIsInScope,
} from "@redotech/redo-fulfillment-wms-common/schema/pick-session";
import { SameBinSortMode } from "@redotech/redo-fulfillment-wms-common/wms-constants-enums";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle_filled.svg";
import { InventoryItemId, inventoryItemIdFormat } from "@redotech/redo-ims-id";
import { LocationId } from "@redotech/redo-inventory-id";
import type { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  ERROR_SOUND,
  NOTIFICATION_SOUND,
  SUCCESS_SOUND,
  SUCCESS_SOUND_2,
} from "@redotech/redo-web/sound/sounds";
import type { ReactNode } from "react";

export const SCAN_CHAR = "&";

/**
 * Shows a prominent error toast at the bottom of the screen with larger text.
 * Auto-dismisses after 3 seconds without blocking user workflow.
 */
export const showPickSessionError = (
  message: string,
  options?: { action?: (dismiss: () => void) => ReactNode; persist?: boolean },
) => {
  const isMobile = window.innerWidth < 768;
  toast(
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: isMobile ? "8px" : "12px",
        fontSize: isMobile ? "18px" : "24px",
        fontWeight: 600,
        padding: isMobile ? "10px 6px" : "16px 10px",
      }}
    >
      <AlertCircleIcon
        style={{
          width: isMobile ? "24px" : "32px",
          height: isMobile ? "24px" : "32px",
          flexShrink: 0,
          color: "#dc2626",
        }}
      />
      {message}
    </div>,
    {
      variant: "error",
      origin: { horizontal: "center", vertical: "top" },
      duration: options?.persist
        ? undefined
        : Temporal.Duration.from({ seconds: 4 }),
      persist: options?.persist,
      hideIcon: true,
      shake: true,
      action: options?.action,
    },
  );
};

export const showPickSessionWarning = (
  message: string,
  options?: { action?: (dismiss: () => void) => ReactNode; persist?: boolean },
) => {
  const isMobile = window.innerWidth < 768;
  toast(
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: isMobile ? "8px" : "12px",
        fontSize: isMobile ? "18px" : "24px",
        fontWeight: 600,
        padding: isMobile ? "10px 6px" : "16px 10px",
      }}
    >
      <AlertCircleIcon
        style={{
          width: isMobile ? "24px" : "32px",
          height: isMobile ? "24px" : "32px",
          flexShrink: 0,
          color: "#d97706",
        }}
      />
      {message}
    </div>,
    {
      variant: "warning",
      origin: { horizontal: "center", vertical: "top" },
      duration: options?.persist
        ? undefined
        : Temporal.Duration.from({ seconds: 4 }),
      persist: options?.persist,
      hideIcon: true,
      action: options?.action,
    },
  );
};

export const showPickSessionSuccess = (
  message: string,
  options?: {
    action?: (dismiss: () => void) => ReactNode;
    dismissPrevious?: boolean;
    persist?: boolean;
    durationSeconds?: number;
  },
) => {
  const isMobile = window.innerWidth < 768;
  toast(
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: isMobile ? "8px" : "12px",
        fontSize: isMobile ? "18px" : "24px",
        fontWeight: 600,
        padding: isMobile ? "10px 6px" : "16px 10px",
      }}
    >
      <CheckCircleIcon
        style={{
          width: isMobile ? "24px" : "32px",
          height: isMobile ? "24px" : "32px",
          flexShrink: 0,
          color: "#16a34a",
        }}
      />
      {message}
    </div>,
    {
      variant: "success",
      origin: { horizontal: "center", vertical: "top" },
      duration: options?.persist
        ? undefined
        : Temporal.Duration.from({ seconds: options?.durationSeconds ?? 4 }),
      persist: options?.persist,
      hideIcon: true,
      action: options?.action,
      dismissPrevious: options?.dismissPrevious,
    },
  );
};

export const playSuccessSound = async () => {
  SUCCESS_SOUND.currentTime = 0;
  void SUCCESS_SOUND.play();
};

export const playSuccessSound2 = async () => {
  SUCCESS_SOUND_2.currentTime = 0;
  void SUCCESS_SOUND_2.play();
};

export const playNotificationSound = async () => {
  NOTIFICATION_SOUND.currentTime = 0;
  void NOTIFICATION_SOUND.play();
};

export const playErrorSound = async () => {
  ERROR_SOUND.currentTime = 0;
  void ERROR_SOUND.play();
};

export function showInventoryLabelRequiredToast({
  onOverride,
  message = "Inventory label required",
  overrideLabel = "Process with SKU label",
}: {
  onOverride: () => void;
  message?: string;
  overrideLabel?: string;
}): void {
  showPickSessionError(message, {
    persist: true,
    action: (dismiss) => (
      <RedoButton
        hierarchy="primary"
        onClick={() => {
          dismiss();
          onOverride();
        }}
        size="sm"
        text={overrideLabel}
      />
    ),
  });
  void playErrorSound();
}

// Hack for FP for now. Update when needed to be more generic.
export function getPickTitle(
  item:
    | PickSessionItem
    | FulfillmentGroupData["items"][number]
    | FulfillmentOrderLineItem,
) {
  const size = item.customAttributes.find((ca) =>
    ca.key.toLowerCase().includes("size"),
  );
  const color = item.customAttributes.find((ca) =>
    ca.key.toLowerCase().includes("color"),
  );

  const customAttributeTitle = [size, color]
    .filter((c) => c !== undefined)
    .map((c) => `${c.key}: ${c.value}`)
    .join(" | ");

  if (customAttributeTitle) {
    return customAttributeTitle;
  }

  const title =
    item.variantTitle === "Default Title"
      ? item.productTitle
      : item.variantTitle;
  if (title) {
    return title;
  }

  // RETURN-kind inventory has no catalog product; fall back to the bigint LP
  // id the unit's QR code encodes, so the picker can visually match the label.
  const id =
    "quantityPicked" in item ? item.inventoryItemId : item.imsInventoryItemId;
  if (!id) {
    return "";
  }
  if (!inventoryItemIdFormat.test(id)) {
    return id;
  }
  return inventoryItemIdFormat.read(id as InventoryItemId).toString();
}

export function isItemMatch(
  item:
    | PickSessionItem
    | FulfillmentGroupData["items"][number]
    | FulfillmentOrderLineItem,
  inputBarcode: string,
  team?: TeamWith<{ outboundLabels: OutboundLabelSettings }> | null,
): boolean {
  // Use only Redo IMS ids here; line items can also carry Shopify inventory ids.
  const itemInvId =
    "quantityPicked" in item
      ? (item as PickSessionItem).inventoryItemId
      : (item as FulfillmentOrderLineItem).imsInventoryItemId;
  if (itemInvId) {
    if (inputBarcode.startsWith("ii_")) {
      return itemInvId === inputBarcode;
    }
    if (/^\d+$/.test(inputBarcode)) {
      try {
        if (
          inventoryItemIdFormat
            .read(itemInvId as InventoryItemId)
            .toString() === inputBarcode
        ) {
          return true;
        }
      } catch {
        // Fall through to regular matching
      }
    }
  } else if (inputBarcode.startsWith("ii_")) {
    // `ii_...` input can only match an inventory-item id; if this item
    // has none, skip the fallback fields (they'd never match anyway).
    return false;
  }

  if (
    item.barcode?.toUpperCase() === inputBarcode.toUpperCase() ||
    item.sku?.toUpperCase() === inputBarcode.toUpperCase() ||
    item.productTitle?.toUpperCase() === inputBarcode.toUpperCase() ||
    `I-${item.variantId}` === inputBarcode.toUpperCase() // specific to CS. CS barcodes are sometimes printed as i-{variantId} even though the variant barcode is a different value.
  ) {
    return true;
  }

  // Allow scanning custom attributes (sometimes extra barcodes are stored in custom attributes)
  if (
    item.customAttributes.some(
      (attr) => attr.value.toUpperCase() === inputBarcode.toUpperCase(),
    )
  ) {
    return true;
  }

  const productCustomMetadata =
    team?.settings?.outboundLabels?.productCustomMetadata;
  if (productCustomMetadata) {
    if (
      productCustomMetadata.productCustomString1?.scannable &&
      item.productCustomString1 &&
      item.productCustomString1.toUpperCase() === inputBarcode.toUpperCase()
    ) {
      return true;
    }
    if (
      productCustomMetadata.productCustomString2?.scannable &&
      item.productCustomString2 &&
      item.productCustomString2.toUpperCase() === inputBarcode.toUpperCase()
    ) {
      return true;
    }
    if (
      productCustomMetadata.productCustomString3?.scannable &&
      item.productCustomString3 &&
      item.productCustomString3.toUpperCase() === inputBarcode.toUpperCase()
    ) {
      return true;
    }
  }

  const variantCustomMetadata =
    team?.settings?.outboundLabels?.variantCustomMetadata;
  if (variantCustomMetadata) {
    if (
      variantCustomMetadata.variantCustomString1?.scannable &&
      item.variantCustomString1 &&
      item.variantCustomString1.toUpperCase() === inputBarcode.toUpperCase()
    ) {
      return true;
    }
    if (
      variantCustomMetadata.variantCustomString2?.scannable &&
      item.variantCustomString2 &&
      item.variantCustomString2.toUpperCase() === inputBarcode.toUpperCase()
    ) {
      return true;
    }
    if (
      variantCustomMetadata.variantCustomString3?.scannable &&
      item.variantCustomString3 &&
      item.variantCustomString3.toUpperCase() === inputBarcode.toUpperCase()
    ) {
      return true;
    }
  }

  return false;
}

export async function getToteByBarcode({
  scannedValue,
  rpcClient,
  locationId,
}: {
  scannedValue: string;
  rpcClient: RedoOutboundLabelsRpcClient;
  locationId?: LocationId | null;
}): Promise<RedoFulfillmentTote | null> {
  let tote: RedoFulfillmentTote | null = null;
  try {
    tote = await rpcClient.getToteByBarcode({
      barcode: scannedValue,
      locationId,
    });
  } catch (error) {
    if ((error as Error)?.message === "Tote not found") {
      showPickSessionError("No matching tote found");
    } else {
      console.error("Error getting tote by barcode", error);
      showPickSessionError("Error finding tote by barcode");
    }
    void playErrorSound();
  }

  return tote;
}

export type ExpandedPickSessionItem = PickSessionItem & {
  cardIndex: number;
  toteId?: string;
};

/**
 * Groups expanded pick session items by variantId and bin.
 * Items are only combined if BOTH match.
 * Returns array with one entry per unique variant+bin combination.
 */

/**
 * Groups pick session items by variantId and bin.
 *
 * - Default / `SKU` mode: all items sharing variant+bin merge into one group,
 *   even if non-adjacent in the server-emitted order.
 * - `ORDER_DATE` mode: only *adjacent* items sharing variant+bin merge, so
 *   FIFO order is preserved when a different SKU sits between two same-SKU
 *   items at the same bin.
 */
export function groupPickSessionItemsByVariantAndBin(
  items: PickSessionItem[],
  sameBinSortMode?: SameBinSortMode,
): { items: PickSessionItem[]; displayItem: PickSessionItem }[] {
  const groupKey = (item: PickSessionItem) =>
    `${item.variantId || item.sku}-${item.bin ?? ""}`;

  const groups: { items: PickSessionItem[]; displayItem: PickSessionItem }[] =
    [];

  if (sameBinSortMode === SameBinSortMode.ORDER_DATE) {
    // Adjacent-run grouping — preserves FIFO between distinct SKU runs.
    let lastKey: string | null = null;
    for (const item of items) {
      const key = groupKey(item);
      const last = groups[groups.length - 1];
      if (last && lastKey === key) {
        last.items.push(item);
      } else {
        groups.push({ items: [item], displayItem: item });
      }
      lastKey = key;
    }
  } else {
    const grouped = new Map<
      string,
      { items: PickSessionItem[]; displayItem: PickSessionItem }
    >();
    for (const item of items) {
      const key = groupKey(item);
      const existing = grouped.get(key);
      if (existing) {
        existing.items.push(item);
      } else {
        grouped.set(key, { items: [item], displayItem: item });
      }
    }
    groups.push(...grouped.values());
  }

  // Sort groups: items without skipIndex first, then items with skipIndex sorted lowest to highest
  return groups.sort((a, b) => {
    const aSkipIndex = a.displayItem.skipIndex;
    const bSkipIndex = b.displayItem.skipIndex;

    // Items without skipIndex come first
    if (aSkipIndex == null && bSkipIndex == null) {
      return 0;
    }
    if (aSkipIndex == null) {
      return -1;
    }
    if (bSkipIndex == null) {
      return 1;
    }

    // Both have skipIndex, sort by skipIndex (lowest first)
    return aSkipIndex - bSkipIndex;
  });
}

export function groupExpandedItemsByVariantAndBin(
  items: ExpandedPickSessionItem[],
  pickSession: PickSession | undefined,
): {
  item: ExpandedPickSessionItem;
  quantity: number;
  totalQuantity: number;
}[] {
  const grouped = new Map<
    string,
    { item: ExpandedPickSessionItem; quantity: number; totalQuantity: number }
  >();

  for (const item of items) {
    const key = `${item.variantId || item.sku}-${item.bin ?? ""}`;

    // Find ALL pick session items with matching variantId and bin
    const matchingItems = pickSession?.items.filter(
      (pi) =>
        (pi.variantId || pi.sku) === (item.variantId || item.sku) &&
        (pi.bin ?? "") === (item.bin ?? ""),
    );

    // Sum totalQuantity from all matching items, subtracting out of stock quantity
    const totalQty =
      matchingItems?.reduce(
        (sum, pi) => sum + pi.totalQuantity - pi.quantityOutOfStock,
        0,
      ) || 1;

    const existing = grouped.get(key);
    if (existing) {
      existing.quantity += 1;
    } else {
      grouped.set(key, { item, quantity: 1, totalQuantity: totalQty });
    }
  }

  return Array.from(grouped.values());
}

export const expandItemsToIndividualCards = (items: PickSessionItem[]) => {
  const expandedItems: ExpandedPickSessionItem[] = [];
  const assignedItems: ExpandedPickSessionItem[] = [];
  const unassignedItems: ExpandedPickSessionItem[] = [];
  const itemsByTote: Record<string, ExpandedPickSessionItem[]> = {};

  if (items) {
    for (const item of items) {
      let globalCardIndex = 0;
      // Flatten totes into an array of toteIds (repeat by quantity)
      const toteAssignments: string[] = [];
      for (const [toteId, { quantity }] of Object.entries(
        item.quantityByTote ?? {},
      )) {
        for (let q = 0; q < quantity; q++) {
          toteAssignments.push(toteId);
        }
      }

      // Create cards for available items (total - out of stock)
      const availableQuantity = item.totalQuantity - item.quantityOutOfStock;

      for (let i = 0; i < availableQuantity; i++) {
        const card: ExpandedPickSessionItem = {
          ...item,
          cardIndex: globalCardIndex,
        };

        expandedItems.push(card);

        if (i < toteAssignments.length) {
          assignedItems.push(card);
          const toteId = toteAssignments[i];
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          (itemsByTote[toteId] ??= []).push(card);
        } else {
          unassignedItems.push(card);
        }

        globalCardIndex++;
      }
    }
  }

  return { expandedItems, unassignedItems, assignedItems, itemsByTote };
};

export function getAvailableToteId({
  pickSession,
  scannedItem,
}: {
  pickSession: PickSession;
  scannedItem: ExpandedPickSessionItem;
}): string | null {
  if (pickSession.strategy === PickSessionStrategy.SINGLE_ITEM) {
    for (const item of pickSession.items) {
      for (const toteId of Object.keys(item.quantityByTote || {})) {
        return toteId;
      }
    }
  } else if (pickSession.strategy === PickSessionStrategy.MULTI_ITEM) {
    for (const item of pickSession.items) {
      if (item.fulfillmentGroupId === scannedItem.fulfillmentGroupId) {
        for (const toteId of Object.keys(item.quantityByTote || {})) {
          // For multi-item strategy, groups can be assigned to multiple totes, so only find the next one that isn't full
          if (!pickSession?.fullTotes?.includes(toteId)) {
            return toteId;
          }
        }
      }
    }
  }

  return null;
}

/**
 * Items expected to be in this tote at the moment the current pick
 * session is marked picked:
 *   - items already in the tote (raw, includes upstream-carried items
 *     that stay put through this session)
 *   - + in-scope items still pending for the same FG/session that will
 *     land in this tote.
 * Cross-zone deferred items (out of scope, not in this tote) belong to
 * a downstream continuation, so they don't count toward this session's
 * tote total.
 */
export function getExpectedToteItemCount({
  pickSession,
  toteId,
  itemsByTote,
  scopedExpandedItems,
}: {
  pickSession: PickSession;
  toteId: string;
  itemsByTote: Record<string, ExpandedPickSessionItem[]>;
  scopedExpandedItems: ExpandedPickSessionItem[];
}): number {
  const items = itemsByTote[toteId] ?? [];
  const isFull = pickSession.fullTotes?.includes(toteId);

  if (pickSession.strategy === PickSessionStrategy.MULTI_ITEM) {
    if (isFull) {
      return items.length;
    }
    if (items.length === 0) {
      return 0;
    }
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const fulfillmentGroupId: string = items[0].fulfillmentGroupId;

    const scopedItemsForFg = scopedExpandedItems.filter(
      (it) => it.fulfillmentGroupId === fulfillmentGroupId,
    ).length;
    const scopedItemsForFgInAnyTote = Object.values(itemsByTote)
      .flat()
      .filter(
        (it) =>
          it.fulfillmentGroupId === fulfillmentGroupId &&
          pickSessionItemIsInScope(pickSession, it),
      ).length;
    const pendingForFg = Math.max(
      0,
      scopedItemsForFg - scopedItemsForFgInAnyTote,
    );

    // When an FG is split across multiple non-full totes, the "still
    // pending" items will (by convention) land in the LAST non-full tote
    // — the one the picker is actively filling — not be split equally
    // across all of them. Without this, both totes show the same
    // inflated expected count (e.g. 2/3 + 2/3 when only 5 are expected).
    // Older non-full totes are treated as "done from the picker's POV"
    // and show items.length only.
    const nonFullToteIdsForFg = Object.entries(itemsByTote)
      .filter(([otherToteId, otherItems]) => {
        if (pickSession.fullTotes?.includes(otherToteId)) {
          return false;
        }
        return otherItems[0]?.fulfillmentGroupId === fulfillmentGroupId;
      })
      .map(([otherToteId]) => otherToteId);
    const activeToteForFg = nonFullToteIdsForFg[nonFullToteIdsForFg.length - 1];
    const pendingLandsHere = toteId === activeToteForFg;

    return items.length + (pendingLandsHere ? pendingForFg : 0);
  }

  // Single-item strategy: one tote per session holds everything in scope.
  const scopedItemsInAnyTote = Object.values(itemsByTote)
    .flat()
    .filter((it) => pickSessionItemIsInScope(pickSession, it)).length;
  const pending = Math.max(
    0,
    scopedExpandedItems.length - scopedItemsInAnyTote,
  );
  return items.length + pending;
}

export function getUnassignedItemCountForFulfillmentGroup({
  pickSession,
  fulfillmentGroupId,
}: {
  pickSession: PickSession;
  fulfillmentGroupId: string;
}): number {
  return pickSession.items
    .filter(
      (item) =>
        item.fulfillmentGroupId === fulfillmentGroupId &&
        pickSessionItemIsInScope(pickSession, item),
    )
    .reduce((sum, item) => {
      const availableQuantity = item.totalQuantity - item.quantityOutOfStock;
      const assignedQuantity = Object.values(item.quantityByTote ?? {}).reduce(
        (toteSum, { quantity }) => toteSum + quantity,
        0,
      );
      return sum + availableQuantity - assignedQuantity;
    }, 0);
}

export function getFirstUnpickedExpandedItem({
  matchedItem,
}: {
  matchedItem: PickSessionItem;
}): ExpandedPickSessionItem {
  // Calculate how many items are still available to pick (excluding picked and out of stock)
  const availableQuantity =
    matchedItem.totalQuantity -
    matchedItem.quantityPicked -
    matchedItem.quantityOutOfStock;

  if (availableQuantity <= 0) {
    throw new Error(
      "All items have already been picked or marked unable to pick",
    );
  }

  const globalCardIndex = 0;

  // The unpicked index should start after the picked items
  const unpickedIndexInGroup = matchedItem.quantityPicked;
  const cardIndex = globalCardIndex + unpickedIndexInGroup;

  // Get tote assignment for this specific card
  const toteAssignments: string[] = [];
  for (const [toteId, { quantity }] of Object.entries(
    matchedItem.quantityByTote ?? {},
  )) {
    for (let q = 0; q < quantity; q++) {
      toteAssignments.push(toteId);
    }
  }

  const toteId = toteAssignments[unpickedIndexInGroup] || undefined;

  return { ...matchedItem, cardIndex, toteId };
}
